import React, { useCallback, useMemo, useState } from 'react';
import { SxProps, Theme } from '@mui/material/styles';
import { AppGrid, AppGridColumn } from './app-grid';
import {
  GridActionToolbarAction,
  GridActionToolbarProps
} from './grid-action-toolbar';
import { AppButtonTone } from '@client/foundation/design-system/components';

export type ManageGridRow = {
  id: string;
};

export type ManageGridColumn<Row extends ManageGridRow> = AppGridColumn<Row>;

export type GridActionSelectionMode = 'none' | 'single' | 'multi';

export type GridActionContext<Row extends ManageGridRow> = {
  selectedIds: string[];
  selectedRows: Row[];
  selectedRow?: Row;
  clearSelection: () => void;
};

export type GridActionConfig<Row extends ManageGridRow> = {
  key: string;
  label: string;
  tone?: AppButtonTone;
  selectionMode?: GridActionSelectionMode;
  disabled?: (context: GridActionContext<Row>) => boolean;
  onClick: (context: GridActionContext<Row>) => void;
};

export type ManageGridProps<Row extends ManageGridRow> = {
  rows: Row[];
  columns: ManageGridColumn<Row>[];
  actions?: GridActionConfig<Row>[];
  paperSx?: SxProps<Theme>;
};

export const ManageGrid = <Row extends ManageGridRow>({
  rows,
  columns,
  actions = [],
  paperSx
}: ManageGridProps<Row>) => {
  const [selected, setSelected] = useState<string[]>([]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selected.includes(row.id)),
    [rows, selected]
  );

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selected[0]),
    [rows, selected]
  );
  const clearSelection = useCallback(() => {
    setSelected([]);
  }, []);

  const actionContext = useMemo<GridActionContext<Row>>(
    () => ({
      selectedIds: selected,
      selectedRows,
      selectedRow,
      clearSelection
    }),
    [clearSelection, selected, selectedRow, selectedRows]
  );

  const resolveSelectionDisabled = useCallback(
    (selectionMode: GridActionSelectionMode = 'none') => {
      if (selectionMode === 'single') {
        return selected.length !== 1;
      }

      if (selectionMode === 'multi') {
        return selected.length < 1;
      }

      return false;
    },
    [selected.length]
  );

  const resolvedActions = useMemo<GridActionToolbarProps['actions']>(
    () =>
      actions.map((action) => ({
        key: action.key,
        label: action.label,
        tone: action.tone,
        disabled:
          resolveSelectionDisabled(action.selectionMode) ||
          action.disabled?.(actionContext) ||
          false,
        onClick: () => action.onClick(actionContext)
      })),
    [actionContext, actions, resolveSelectionDisabled]
  );

  return (
    <AppGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      selectedRowIds={selected}
      onSelectedRowIdsChange={setSelected}
      multiSelect={true}
      actions={resolvedActions}
      paperSx={paperSx}
    />
  );
};
